import { FC, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetCIUsForClient } from '../../../../api/client';
import {
  useGetAllKidTemplates,
  useGetKidTemplateAffectedShareClasses,
  useGetKidTemplateIncompleteDocuments,
} from '../../../../api/kids/hooks/queries';
import {
  useDeactivateKidTemplate,
  useDowngradeKidTemplate,
  useUpdateKidTemplateContent,
} from '../../../../api/kids/hooks/mutations';
import {
  KidTemplateIncompleteDocumentsResponse,
  KidTemplateType,
  KidTemplateSetupStatus,
  getKidTemplateTypeRank,
} from '../../../../api/kids/types';
import { useGetClientAdministrators } from '../../../../api/administrator';
import { useFunds } from '../../../../api/funds';
import { useAllFundManagersForClient } from '../../../../api/rs3-public/raptorPlatform';
import TokenService from '../../../../services/token-service';
import {
  createPageDetailsSelector,
  createSectionByIdSelector,
} from '../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { createResourceCacheKey } from '../../../layout/general/SimpleGeneralPageWrapper';
import { sortFundNames } from '../../../../utilities/customSorting';
import { RaidrV2RoutePaths } from '../../../../routes/page-routes';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../redux/ui/controlbar/actions';
import BackToOverviewBtn from '../../raidr/components/buttons/BackToOverviewBtn';
import EditTemplatePanel from './components/EditTemplatePanel';
import {
  pickRawTemplateType,
  pickTemplateSetupStatus,
  resolveTemplateGroupingTypeKey,
  resolveTemplateSetupStatusFromCoverage,
} from './templateManagement.helpers';
import {
  resolveTemplateReferenceLabel,
  toSelectableKidTemplate,
  toTemplateEditorFormatOptions,
} from './templateManagement.utils';
import {
  KID_TEMPLATE_TYPE_VALUE_SET,
  TEMPLATE_TYPES,
  formatEditTemplateScopeSubtitle,
} from './templateManagement.constants';
import { AddTemplateToDocumentContext } from './templateManagement.types';
import { EMPTY_KID_TEMPLATE_AFFECTED_ROWS } from './templateDocumentRows.utils';
import type { FormatOptions } from '../../raidr/kidsV2/types/KidPdfGeneratorV2.types';

const TemplateEditPage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateIdFromUrl = searchParams.get('templateId') || '';
  const user = TokenService.getUser();
  const clientId = user?.user_client_id || '';
  const userId = user?.user_id || '';
  const sectionDetails = useSelector(createSectionByIdSelector('raidr-v2'));
  const pageDetails = useSelector(
    createPageDetailsSelector('template-management', 'raidr-v2'),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const goToTemplateManagement = useCallback(() => {
    navigate(RaidrV2RoutePaths.TEMPLATE_MANAGEMENT);
  }, [navigate]);

  const { data: allTemplatesData, isFetched: templatesFetched } =
    useGetAllKidTemplates(clientId);
  const { data: fundsData } = useFunds({
    fundTypes: sectionDetails?.fundTypes,
    rsResources,
    raptorPlatformId,
  });
  const { data: fundManagersData } = useAllFundManagersForClient(clientId);
  const { data: ciusData } = useGetCIUsForClient(clientId);
  const { data: administratorsData } = useGetClientAdministrators(clientId);

  const templates = useMemo(
    () => (Array.isArray(allTemplatesData) ? allTemplatesData : []),
    [allTemplatesData],
  );
  const availableFunds = useMemo(
    () =>
      [...(fundsData || [])].sort((a, b) =>
        sortFundNames(a.fund_name || '', b.fund_name || ''),
      ),
    [fundsData],
  );
  const availableFundManagers = useMemo(
    () =>
      [...(fundManagersData || [])].sort((a, b) =>
        a.fund_manager_name.localeCompare(b.fund_manager_name),
      ),
    [fundManagersData],
  );
  const availableCIUs = useMemo(
    () => [...(ciusData || [])].sort((a, b) => a.ciu_name.localeCompare(b.ciu_name)),
    [ciusData],
  );
  const availableAdministrators = useMemo(
    () =>
      [...(administratorsData || [])].sort((a, b) =>
        a.administrator_name.localeCompare(b.administrator_name),
      ),
    [administratorsData],
  );

  const selectedTemplate = useMemo(
    () =>
      templates.find((template) => template.template_id === templateIdFromUrl) ||
      null,
    [templates, templateIdFromUrl],
  );
  const activeTemplateId = selectedTemplate?.template_id ?? '';

  useEffect(() => {
    if (!templateIdFromUrl) {
      goToTemplateManagement();
      return;
    }
    if (!templatesFetched) return;
    if (!selectedTemplate) {
      goToTemplateManagement();
    }
  }, [
    templateIdFromUrl,
    templatesFetched,
    selectedTemplate,
    goToTemplateManagement,
  ]);

  const addTemplateContext = useMemo<AddTemplateToDocumentContext | undefined>(
    () =>
      selectedTemplate
        ? {
            clientId,
            userId,
            template: toSelectableKidTemplate(selectedTemplate),
          }
        : undefined,
    [clientId, selectedTemplate, userId],
  );

  const incompleteDocumentsQuery = useGetKidTemplateIncompleteDocuments(
    clientId,
    undefined,
    undefined,
    { enabled: !!clientId },
  );

  const updateTemplateContentMutation = useUpdateKidTemplateContent(
    clientId,
    activeTemplateId,
  );
  const downgradeTemplateMutation = useDowngradeKidTemplate(
    clientId,
    activeTemplateId,
  );
  const deactivateTemplateMutation = useDeactivateKidTemplate(
    clientId,
    activeTemplateId,
  );

  const affectedDocumentsQuery = useGetKidTemplateAffectedShareClasses(
    clientId,
    activeTemplateId,
    undefined,
    { enabled: !!clientId && !!activeTemplateId },
  );

  const incompleteDocuments: KidTemplateIncompleteDocumentsResponse =
    incompleteDocumentsQuery.data || [];

  const templateTypeFromIncompleteById = useMemo(() => {
    const m = new Map<string, unknown>();
    incompleteDocuments.forEach((entry) => {
      if (!entry.template_id || m.has(entry.template_id)) return;
      const raw = pickRawTemplateType(entry.template);
      if (raw !== undefined && raw !== null && raw !== '') {
        m.set(entry.template_id, raw);
      }
    });
    return m;
  }, [incompleteDocuments]);

  const incompleteEntryByTemplateId = useMemo(() => {
    const m = new Map<string, KidTemplateIncompleteDocumentsResponse[number]>();
    incompleteDocuments.forEach((entry) => {
      if (entry.template_id) m.set(entry.template_id, entry);
    });
    return m;
  }, [incompleteDocuments]);

  const templateSetupStatusFromIncompleteById = useMemo(() => {
    const m = new Map<string, string>();
    incompleteDocuments.forEach((entry) => {
      const setupStatus = pickTemplateSetupStatus(entry.template);
      if (entry.template_id && setupStatus) {
        m.set(entry.template_id, setupStatus);
      }
    });
    return m;
  }, [incompleteDocuments]);

  const selectedTemplateSetupStatus = useMemo(() => {
    if (!selectedTemplate) return null;
    return resolveTemplateSetupStatusFromCoverage({
      templateStatus:
        pickTemplateSetupStatus(selectedTemplate) ||
        templateSetupStatusFromIncompleteById.get(selectedTemplate.template_id) ||
        null,
      incompleteEntry: incompleteEntryByTemplateId.get(
        selectedTemplate.template_id,
      ),
      coverageKnown: incompleteDocumentsQuery.isSuccess,
    });
  }, [
    selectedTemplate,
    templateSetupStatusFromIncompleteById,
    incompleteEntryByTemplateId,
    incompleteDocumentsQuery.isSuccess,
  ]);

  const selectedTemplateIncompleteEntry = selectedTemplate
    ? incompleteEntryByTemplateId.get(selectedTemplate.template_id)
    : undefined;
  const selectedTemplateMissingDocuments =
    selectedTemplateIncompleteEntry?.missing_overviews ??
    EMPTY_KID_TEMPLATE_AFFECTED_ROWS;
  const selectedTemplateInvalidDocuments =
    selectedTemplateIncompleteEntry?.invalid_overviews ??
    EMPTY_KID_TEMPLATE_AFFECTED_ROWS;
  const selectedTemplateMissingDocumentCount =
    selectedTemplateMissingDocuments.length;
  const selectedTemplateInvalidDocumentCount =
    selectedTemplateInvalidDocuments.length;
  const selectedTemplateIsIncomplete =
    selectedTemplateSetupStatus === KidTemplateSetupStatus.INCOMPLETE;
  const canUpdateTemplateContent =
    !!activeTemplateId &&
    !selectedTemplateIsIncomplete &&
    (!selectedTemplateSetupStatus ||
      selectedTemplateSetupStatus === KidTemplateSetupStatus.COMPLETE);

  const currentTemplateTypeForRank = useMemo((): KidTemplateType | null => {
    if (!selectedTemplate) return null;
    const key = resolveTemplateGroupingTypeKey(
      selectedTemplate,
      templateTypeFromIncompleteById.get(selectedTemplate.template_id),
    );
    if (KID_TEMPLATE_TYPE_VALUE_SET.has(key)) {
      return key as KidTemplateType;
    }
    return null;
  }, [selectedTemplate, templateTypeFromIncompleteById]);

  const allowedDowngradeTypes = useMemo((): KidTemplateType[] => {
    if (!currentTemplateTypeForRank) return [];
    const currentRank = getKidTemplateTypeRank(currentTemplateTypeForRank);
    const next = TEMPLATE_TYPES.filter((t) => getKidTemplateTypeRank(t) < currentRank);
    next.sort((a, b) => getKidTemplateTypeRank(b) - getKidTemplateTypeRank(a));
    return next;
  }, [currentTemplateTypeForRank]);

  const selectedTemplateReferenceLabel = useMemo(() => {
    if (!selectedTemplate || !currentTemplateTypeForRank) return null;
    if (currentTemplateTypeForRank === KidTemplateType.MANCO) return null;
    return resolveTemplateReferenceLabel(
      currentTemplateTypeForRank,
      selectedTemplate.reference_id,
      {
        availableFunds,
        availableFundManagers,
        availableCIUs,
        availableAdministrators,
      },
    );
  }, [
    availableAdministrators,
    availableCIUs,
    availableFundManagers,
    availableFunds,
    currentTemplateTypeForRank,
    selectedTemplate,
  ]);

  const selectedTemplateContent = selectedTemplate?.template_content || '';
  const selectedTemplateFormatOptions = useMemo<FormatOptions>(
    () => toTemplateEditorFormatOptions(selectedTemplate?.format_options),
    [selectedTemplate],
  );

  useEffect(() => {
    dispatch(toggleControlBarShowing(true));
    dispatch(
      updateControlBarComponents([
        <BackToOverviewBtn
          key="back_to_templates"
          backToOverview={goToTemplateManagement}
          dontSetPosition
        />,
        <div
          key="tm-edit-title"
          className="ml-4 flex min-w-0 flex-1 items-center pr-2"
        >
          <h1 className="truncate text-xl font-semibold text-mainblue md:text-2xl">
            Edit template
          </h1>
        </div>,
      ]),
    );
    return () => {
      dispatch(updateControlBarComponents([]));
      dispatch(toggleControlBarShowing(false));
    };
  }, [
    dispatch,
    goToTemplateManagement,
  ]);

  if (!selectedTemplate || !activeTemplateId) {
    return (
      <div className="flex h-[calc(100vh-8.75rem)] items-center justify-center text-sm text-gray-500">
        Loading template…
      </div>
    );
  }

  return (
    <div className="-m-2.5 flex h-[calc(100vh-8.75rem)] w-[calc(100%+1.25rem)] flex-col overflow-hidden text-sm text-gray-900">
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-3 pt-3">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <EditTemplatePanel
            key={activeTemplateId}
            clientId={clientId}
            templateName={selectedTemplate.template_name}
            scopeSubtitle={
              currentTemplateTypeForRank
                ? formatEditTemplateScopeSubtitle(
                    currentTemplateTypeForRank,
                    selectedTemplateReferenceLabel,
                  )
                : null
            }
            selectedTemplateId={activeTemplateId}
            initialContent={selectedTemplateContent}
            initialFormatOptions={selectedTemplateFormatOptions}
            allowedDowngradeTypes={allowedDowngradeTypes}
            currentTemplateTypeForRank={currentTemplateTypeForRank}
            templateSetupStatus={selectedTemplateSetupStatus}
            missingDocumentCount={selectedTemplateMissingDocumentCount}
            invalidDocumentCount={selectedTemplateInvalidDocumentCount}
            isCheckingDocumentCoverage={incompleteDocumentsQuery.isFetching}
            canUpdateContent={canUpdateTemplateContent}
            missingDocuments={selectedTemplateMissingDocuments}
            invalidDocuments={selectedTemplateInvalidDocuments}
            affectedDocumentsQuery={affectedDocumentsQuery}
            addTemplateContext={addTemplateContext}
            updateTemplateContentMutation={updateTemplateContentMutation}
            downgradeTemplateMutation={downgradeTemplateMutation}
            deactivateTemplateMutation={deactivateTemplateMutation}
            onDeactivated={goToTemplateManagement}
            availableFunds={availableFunds}
            availableFundManagers={availableFundManagers}
            availableCIUs={availableCIUs}
            availableAdministrators={availableAdministrators}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateEditPage;
